import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { useCallback, useEffect, useRef } from 'react';
import * as Tone from 'tone';
import MockTimeKeeper from 'Models/MockTimeKeeper';
import TimeSignature from 'Models/TimeSignature';

const DEFAULT_TIME_SIGNATURE = new TimeSignature(4, 4);

type UseDraftPlaybackTimeKeeperArgs = {
  bpm: number;
  isPlaying: boolean;
  cursorIsVisible: boolean;
};

const TICK_NOTE_TYPE = NoteType._32nd;

export default function useDraftPlaybackTimeKeeper({
  bpm,
  isPlaying,
  cursorIsVisible,
}: UseDraftPlaybackTimeKeeperArgs) {
  const timeKeeperRef = useRef<MockTimeKeeper | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const playbackAnchorAudioSecRef = useRef<number | null>(null);
  const pausedTimestampRef = useRef(0);

  if (!timeKeeperRef.current) {
    const timeKeeper = new MockTimeKeeper();
    timeKeeper.setTimeSignatures(0, [DEFAULT_TIME_SIGNATURE]);
    timeKeeper.setCurrentTime(0);
    timeKeeperRef.current = timeKeeper;
  }

  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current != null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const configurePausedState = useCallback((tempoBpm: number, pausedTimestamp: number) => {
    const timeKeeper = timeKeeperRef.current;
    if (!timeKeeper) return 0;

    timeKeeper.unpause(tempoBpm, 0, 0, TICK_NOTE_TYPE);
    const pausedAudioTimeSec = timeKeeper.measureTimestampToAudioTime(pausedTimestamp);
    timeKeeper.setCurrentTime(pausedAudioTimeSec);
    timeKeeper.pause();
    return pausedAudioTimeSec;
  }, []);

  const syncPlayingTime = useCallback(() => {
    const timeKeeper = timeKeeperRef.current;
    const playbackAnchorAudioSec = playbackAnchorAudioSecRef.current;
    if (!timeKeeper || playbackAnchorAudioSec == null) return;

    const elapsedAudioSec = Math.max(0, Tone.now() - playbackAnchorAudioSec);
    timeKeeper.setCurrentTime(elapsedAudioSec);
  }, []);

  const reset = useCallback(() => {
    const timeKeeper = timeKeeperRef.current;
    cancelAnimation();
    playbackAnchorAudioSecRef.current = null;
    pausedTimestampRef.current = 0;

    if (!timeKeeper) return;
    configurePausedState(bpm, 0);
    timeKeeper.setCurrentTime(0);
  }, [bpm, cancelAnimation, configurePausedState]);

  useEffect(() => {
    if (!cursorIsVisible) {
      reset();
    }
  }, [cursorIsVisible, reset]);

  useEffect(() => {
    const timeKeeper = timeKeeperRef.current;
    if (!timeKeeper || !cursorIsVisible) return;

    if (isPlaying) {
      timeKeeper.setBpm(bpm);
      return;
    }

    const pausedAudioTimeSec = configurePausedState(bpm, pausedTimestampRef.current);
    timeKeeper.setCurrentTime(pausedAudioTimeSec);
  }, [bpm, configurePausedState, cursorIsVisible, isPlaying]);

  useEffect(() => {
    const timeKeeper = timeKeeperRef.current;
    if (!timeKeeper || !cursorIsVisible) return;

    if (isPlaying) {
      const pausedAudioTimeSec = configurePausedState(bpm, pausedTimestampRef.current);
      timeKeeper.unpause(bpm, 0, 0, TICK_NOTE_TYPE);
      timeKeeper.setCurrentTime(pausedAudioTimeSec);
      playbackAnchorAudioSecRef.current = Tone.now() - pausedAudioTimeSec;
      syncPlayingTime();

      const animate = () => {
        syncPlayingTime();
        animationFrameRef.current = window.requestAnimationFrame(animate);
      };

      cancelAnimation();
      animationFrameRef.current = window.requestAnimationFrame(animate);
      return;
    }

    cancelAnimation();
    syncPlayingTime();
    pausedTimestampRef.current = timeKeeper.audioTimeToMeasureTimestamp();
    timeKeeper.pause();
    playbackAnchorAudioSecRef.current = null;
  }, [bpm, cancelAnimation, configurePausedState, cursorIsVisible, isPlaying, syncPlayingTime]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  return timeKeeperRef.current;
}
